import { computed, ref, watch } from "vue";

import { almConfig } from "@/almConfig";
import { createAlmHandler } from "@/handlers/almHandler";
import { captureExceptionWithContext } from "@/sentry";
import { useAppStore } from "@/stores";
import { useToastStore } from "@/stores/toast";

/**
 * A PI session's ALM connection: its name, the cached mapping warnings, and the
 * two checks that refresh them.
 *
 * @param {() => object | null} session - a sessions-list row, or null
 * @param {object} inSession - which of the connection's rows this session holds,
 *   since both checks cover the whole connection
 * @param {() => number[]} inSession.teamIds
 * @param {() => number[]} inSession.artIds
 */
export function useSessionAlm(session, { teamIds, artIds }) {
  const store = useAppStore();
  const toastStore = useToastStore();

  const connectionName = ref("");
  const erroredTeamIds = ref([]);
  const erroredArtIds = ref([]);

  const alm = computed(() =>
    session()?.connectionId
      ? {
          almType: session().almTool,
          connectionId: session().connectionId,
          status: session().almStatus,
          name: connectionName.value,
        }
      : null,
  );

  const checksUnsupported = computed(
    () => !!alm.value && !almConfig(alm.value?.almType).mappingErrorCheck,
  );
  // Only an admin can act on a reported error
  const canCheck = computed(() => !!alm.value && !checksUnsupported.value && store.isAdmin);

  // `createAlmHandler` keys off an ALM config object; a sessions-list row names
  // the same things differently
  function handlerFor(row) {
    return createAlmHandler({
      alm_connection_id: row.connectionId,
      alm_connection_type: row.almTool,
      sync_session_id: row.sync_session_id,
    });
  }

  function requireHandler() {
    const handler = handlerFor(session());
    if (!handler) throw new Error("No connection to the ALM tool.");
    return handler;
  }

  // Monotonic guard: a newer connection must not lose to an older answer
  let seq = 0;

  async function load(row, mine) {
    const handler = handlerFor(row);
    if (!handler) return;

    const name = await handler.getName();
    if (mine === seq && typeof name === "string") connectionName.value = name;

    // Cached peek only: the live check is expensive and stays on the buttons
    if (!almConfig(row.almTool).mappingErrorCheck) return;
    const [teams, arts] = await Promise.all([
      handler.checkAllTeamMappings({ cachedOnly: true }),
      handler.checkAllProjectPermissionsForArt({ cachedOnly: true }),
    ]);
    if (mine !== seq) return;
    if (teams?.checked) erroredTeamIds.value = teams.errored_teams ?? [];
    if (arts?.checked) erroredArtIds.value = arts.arts_with_missing_permissions ?? [];
  }

  // The sessions list mutates `connectionId` onto the row after its own request,
  // so this cannot ride on the row's identity changing
  watch(
    () => [session()?.session_id, session()?.connectionId],
    ([, connectionId]) => {
      const mine = ++seq;
      connectionName.value = "";
      erroredTeamIds.value = [];
      erroredArtIds.value = [];
      if (session() && connectionId) void load(session(), mine);
    },
    { immediate: true },
  );

  // Team and ART mapping are separate areas of a connection's setup, so they are
  // checked separately - which one is at fault is what says where to look. Only
  // rows in this session are counted: the checks cover the whole connection.
  function report(errored, mine, [one, many], clean) {
    const hits = mine.filter((id) => errored.includes(id)).length;
    if (!hits) {
      toastStore.showMessage({ message: clean });
      return;
    }
    toastStore.showMessage({
      message: `${hits} ${hits === 1 ? one : many} in this PI session ${
        hits === 1 ? "has" : "have"
      } configuration errors - see the warnings in the list.`,
      type: "error",
    });
  }

  // The caller's button only tracks a spinner, so a rejection would leave the
  // click with nothing to show for it
  function reportFailure(error) {
    void captureExceptionWithContext(error);
    toastStore.showMessage({
      message: `Could not check the mapping: ${error.message}`,
      type: "error",
    });
  }

  async function checkTeamMappings() {
    try {
      const res = await requireHandler().checkAllTeamMappings({ cachedOnly: false });
      erroredTeamIds.value = res?.checked ? (res.errored_teams ?? []) : [];
      report(erroredTeamIds.value, teamIds(), ["team", "teams"], "No team mapping errors found.");
    } catch (error) {
      reportFailure(error);
    }
  }

  async function checkArtMappings() {
    try {
      const res = await requireHandler().checkAllProjectPermissionsForArt({ cachedOnly: false });
      erroredArtIds.value = res?.checked ? (res.arts_with_missing_permissions ?? []) : [];
      report(erroredArtIds.value, artIds(), ["ART", "ARTs"], "No ART mapping errors found.");
    } catch (error) {
      reportFailure(error);
    }
  }

  return {
    alm,
    erroredTeamIds,
    erroredArtIds,
    canCheck,
    checksUnsupported,
    checkTeamMappings,
    checkArtMappings,
  };
}
